'use client';

import React, { useState, useEffect, useRef } from 'react';
import TransitionLink from './TransitionLink';
import { useParams, usePathname } from 'next/navigation';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import dynamic from 'next/dynamic';
import SplashButton from './SplashButton';
import AnimatedHamburgerButton from './AnimatedHamburgerButton';
import { MobileMenu } from './MobileMenu';
import { MegaMenuShell } from './MegaMenuShell';
import { IntegrationsNavMenu } from './IntegrationsNavMenu';
import { NavServicesMega } from './NavServicesMega';
import Image from 'next/image';
import { PrismicNextImage } from '@prismicio/next';
import type { NavLabels, NavSection, NavUtility } from '@/lib/get-navbar-data';

const DEFAULT_MOBILE_MENU_TOP = 76;

const navLinkClass = (active: boolean, isOpen: boolean) =>
  [
    'nav-label ga-menu group flex items-center text-sm tracking-[0.7px] transition-[colors,opacity] hover:text-gray-200 hover:opacity-100 xl:text-[13px]',
    active ? 'active font-normal text-white' : 'font-normal text-white opacity-80',
    isOpen ? 'dropdown-open' : '',
  ].join(' ');

const StickyHeadroom = dynamic(() => import('./Headroomy'), {
  ssr: false,
});

const openTawkChat = () => {
  const api = typeof window !== 'undefined' ? (window.Tawk_API as Record<string, unknown> | undefined) : undefined;
  const maximize = api?.maximize;
  if (typeof maximize === 'function') {
    (maximize as () => void)();
  }
};

export function Navbar({ sections, utility, labels }: { sections: NavSection[]; utility: NavUtility; labels: NavLabels }) {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileMenuTop, setMobileMenuTop] = useState(DEFAULT_MOBILE_MENU_TOP);
  const params = useParams();
  const lang = params.lang as string;
  const pathname = usePathname();
  const isContactPage = /\/contact\/?$/.test(pathname);
  // Pages with a white background need the inverted (dark-on-light) nav while
  // it sits static at the top; once pinned it gets its dark backdrop back.
  const isLightNav = /\/guides\/|\/netsuite-pricing\/?$|\/blog\/?$|\/blog\/category\//.test(pathname);

  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLElement | null>(null);

  const isMenuOpen = (header: string) => openMenu === header;
  const isMobileOpen = openMenu === '__mobile__';
  const isAnyDropdownOpen = openMenu !== null && openMenu !== '__mobile__';

  const isActive = (href: string) => pathname.startsWith(href);

  const closeMenu = () => setOpenMenu(null);

  const openDropdown = (key: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(key);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 150);
  };

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : '';
    if (!isMobileOpen) {
      return () => {
        document.body.style.overflow = '';
      };
    }

    const updateMobileMenuBounds = () => {
      const visualViewport = window.visualViewport;
      const viewportBottom = visualViewport
        ? visualViewport.offsetTop + visualViewport.height
        : window.innerHeight;
      const navBottom = navRef.current?.getBoundingClientRect().bottom ?? DEFAULT_MOBILE_MENU_TOP;
      const top = Math.max(0, Math.min(Math.ceil(navBottom), Math.ceil(viewportBottom)));

      setMobileMenuTop((prev) => (prev === top ? prev : top));
    };

    updateMobileMenuBounds();
    const frame = window.requestAnimationFrame(updateMobileMenuBounds);
    const visualViewport = window.visualViewport;

    window.addEventListener('resize', updateMobileMenuBounds);
    window.addEventListener('orientationchange', updateMobileMenuBounds);
    visualViewport?.addEventListener('resize', updateMobileMenuBounds);
    visualViewport?.addEventListener('scroll', updateMobileMenuBounds);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateMobileMenuBounds);
      window.removeEventListener('orientationchange', updateMobileMenuBounds);
      visualViewport?.removeEventListener('resize', updateMobileMenuBounds);
      visualViewport?.removeEventListener('scroll', updateMobileMenuBounds);
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  const logoSection = sections.find((s) => s.type === 'logo');
  const ctaSections = sections.filter((s): s is Extract<NavSection, { type: 'cta' }> => s.type === 'cta');
  // Desktop hides chat-action CTAs (e.g. "Chat with us") — those are mobile-only.
  const desktopCtaSections = ctaSections.filter((c) => c.action !== 'open_chat');
  const desktopSections = sections.filter((s) => s.type !== 'logo' && s.type !== 'cta');

  const renderCta = (cta: Extract<NavSection, { type: 'cta' }>, key: string) => {
    const color = cta.style === 'ghost' ? 'transparent' : 'red';
    const isChat = cta.action === 'open_chat';
    const sharedProps = {
      className: `nav-contact ga-contact transition-opacity duration-700 sm:min-w-[140px] ${
        cta.style === 'ghost' ? 'nav-cta-ghost' : 'nav-cta-primary'
      }`,
      size: 'cm' as const,
      'data-menu': cta.header,
      id: `nav-cta-${key}`,
      color: color as 'transparent' | 'red',
      text: cta.header,
      'data-location': 'Navigation',
    };
    if (isChat) {
      return (
        <SplashButton
          key={key}
          {...sharedProps}
          as="button"
          onClick={(e) => {
            e.preventDefault();
            openTawkChat();
          }}
        />
      );
    }
    return <SplashButton key={key} {...sharedProps} url={`/${lang}${cta.url}`} />;
  };

  return (
    <>
      <StickyHeadroom scrollHeight={100} pinStart={40} zIndex={30} className="navbar">
        <motion.nav
          ref={navRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          aria-label="Main navigation"
          className={`relative z-[100] bg-[rgba(68,46,46,0.01)] py-4 shadow-sm backdrop-blur ${
            isAnyDropdownOpen || isMobileOpen ? 'menu-open' : ''
          } ${isLightNav ? 'nav-light' : ''}`}
        >
          <div className="container mx-auto flex items-center justify-between">
            {/* Mobile hamburger */}
            <div className="lg:hidden">
              <div className="mr-4 flex items-center">
                <AnimatedHamburgerButton
                  isOpen={isMobileOpen}
                  onClick={() => setOpenMenu((prev) => (prev === '__mobile__' ? null : '__mobile__'))}
                />
              </div>
            </div>

            {/* Logo */}
            <div className="flex flex-1 items-center lg:flex-initial">
              <TransitionLink
                href={`/${lang}${logoSection?.url ?? '/'}`}
                data-menu="Home Logo"
                className="logo-link ga-menu-home inline-flex items-center text-xl font-normal uppercase leading-none text-white transition-opacity duration-700 ease-in-out lg:mr-8 se:text-xl"
              >
                {logoSection?.type === 'logo' && logoSection.logoLight?.url ? (
                  <>
                    <PrismicNextImage
                      field={logoSection.logoLight}
                      className={`h-auto w-auto max-h-12 max-w-[180px] ${
                        isLightNav && logoSection.logoDark?.url
                          ? 'hidden [.headroomy-pinned_&]:block'
                          : isContactPage && logoSection.logoDark?.url
                            ? 'block lg:hidden lg:[.headroomy-pinned_&]:block'
                            : 'block'
                      }`}
                    />
                    {isLightNav && logoSection.logoDark?.url && (
                      <PrismicNextImage
                        field={logoSection.logoDark}
                        className="block h-auto w-auto max-h-12 max-w-[180px] [.headroomy-pinned_&]:hidden"
                      />
                    )}
                    {isContactPage && logoSection.logoDark?.url && (
                      <PrismicNextImage
                        field={logoSection.logoDark}
                        className="hidden h-auto w-auto max-h-12 max-w-[180px] lg:block lg:[.headroomy-pinned_&]:hidden"
                      />
                    )}
                  </>
                ) : (
                  <>
                    <span className="logo-one">
                      <span className="logo-cap">O</span>ne
                    </span>
                    Pacific
                  </>
                )}
              </TransitionLink>
            </div>

            {/* Desktop nav */}
            <div className="desktop-nav hidden h-full space-x-10 lg:flex xl:space-x-16">
              {desktopSections.map((section) => {
                if (section.type === 'link') {
                  return (
                    <div key={section.header} className="relative">
                      <TransitionLink
                        href={`/${lang}${section.url}`}
                        data-menu={section.header}
                        className={navLinkClass(isActive(`/${lang}${section.url}`), false)}
                      >
                        {section.header}
                      </TransitionLink>
                    </div>
                  );
                }

                if (section.type === 'standard' || section.type === 'icon_row' || section.type === 'integrations') {
                  const open = isMenuOpen(section.header);
                  const hubUrl = section.url ? `/${lang}${section.url}` : null;
                  return (
                    <div
                      key={section.header}
                      className={`relative${section.header === 'Resources' ? ' hide-below-1100' : ''}`}
                      onMouseEnter={() => openDropdown(section.header)}
                      onMouseLeave={scheduleClose}
                    >
                      {hubUrl ? (
                        <TransitionLink
                          href={hubUrl}
                          data-menu={section.header}
                          className={navLinkClass(isActive(hubUrl), open)}
                        >
                          {section.header}
                          <ChevronDown
                            className={`ml-1 h-4 w-4 transition-all duration-200 ease-in-out ${
                              open ? 'rotate-180' : ''
                            } group-hover:translate-y-[2px]`}
                          />
                        </TransitionLink>
                      ) : (
                        <span
                          data-menu={section.header}
                          className={navLinkClass(false, open)}
                        >
                          {section.header}
                          <ChevronDown
                            className={`ml-1 h-4 w-4 transition-all duration-200 ease-in-out ${
                              open ? 'rotate-180' : ''
                            } group-hover:translate-y-[2px]`}
                          />
                        </span>
                      )}
                    </div>
                  );
                }

                return null;
              })}
            </div>

            {/* CTA buttons (desktop hides chat-action CTAs) */}
            {desktopCtaSections.length > 0 && (
              <div className="flex items-center gap-2">
                {desktopCtaSections.map((cta, i) => renderCta(cta, `${cta.header}-${i}`))}
              </div>
            )}
          </div>

          {/* Desktop mega menus */}
          {desktopSections.map((section) => {
            if (section.type === 'standard') {
              return (
                <MegaMenuShell
                  key={section.header}
                  isOpen={isMenuOpen(section.header)}
                  onClose={closeMenu}
                  onMouseEnter={() => openDropdown(section.header)}
                  onMouseLeave={scheduleClose}
                >
                  <NavServicesMega section={section} lang={lang} onClose={closeMenu} labels={labels} />
                </MegaMenuShell>
              );
            }

            if (section.type === 'icon_row') {
              return (
                <MegaMenuShell
                  key={section.header}
                  isOpen={isMenuOpen(section.header)}
                  onClose={closeMenu}
                  onMouseEnter={() => openDropdown(section.header)}
                  onMouseLeave={scheduleClose}
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div className="col-span-1 flex flex-col justify-start pr-6">
                      {section.menuTitle && (
                        <h3 className="mb-4 text-3xl font-normal leading-[1.15] text-black/80">
                          {section.menuTitle}
                        </h3>
                      )}
                    </div>
                    <div className="col-span-3 grid grid-cols-1 gap-4 md:grid-cols-3">
                      {section.items.map((item, i) => (
                        <TransitionLink
                          key={item.url}
                          href={`/${lang}${item.url}`}
                          className="group flex items-center gap-2 rounded-lg border border-transparent bg-white p-2 transition-all duration-200 hover:border-dashed hover:border-blue-400 hover:bg-transparent"
                          onClick={closeMenu}
                          style={{ animation: 'fadeInUp 0.3s ease-out both', animationDelay: `${i * 40}ms` }}
                        >
                          {item.icon && (
                            <Image
                              src={item.icon}
                              alt={item.name}
                              width={48}
                              height={48}
                              className="h-12 w-12 rounded object-contain"
                              priority={false}
                            />
                          )}
                          <span className="text-base font-normal text-blue-800">
                            {item.name}
                          </span>
                        </TransitionLink>
                      ))}
                    </div>
                  </div>
                </MegaMenuShell>
              );
            }

            if (section.type === 'integrations') {
              return (
                <MegaMenuShell
                  key={section.header}
                  isOpen={isMenuOpen(section.header)}
                  onClose={closeMenu}
                  onMouseEnter={() => openDropdown(section.header)}
                  onMouseLeave={scheduleClose}
                >
                  <IntegrationsNavMenu
                    categories={section.categories}
                    lang={lang}
                    onClose={closeMenu}
                    primaryTitle={section.primaryTitle}
                    secondaryTitle={section.secondaryTitle}
                    viewAllButton={section.viewAllButton}
                    totalCount={section.totalCount}
                  />
                </MegaMenuShell>
              );
            }

            return null;
          })}

          {/* Preload icon_row icons (hidden) */}
          <div style={{ display: 'none' }}>
            {desktopSections
              .filter((s): s is Extract<NavSection, { type: 'icon_row' }> => s.type === 'icon_row')
              .flatMap((s) => s.items)
              .filter((item) => item.icon)
              .map((item) => (
                <Image
                  key={item.url}
                  src={item.icon}
                  alt={item.name}
                  width={48}
                  height={48}
                  priority={false}
                />
              ))}
          </div>
        </motion.nav>
      </StickyHeadroom>

      <MobileMenu
        isOpen={isMobileOpen}
        sections={sections}
        utility={utility}
        labels={labels}
        lang={lang}
        topOffset={mobileMenuTop}
        onLinkClick={closeMenu}
        onOpenChat={openTawkChat}
      />

      <AnimatePresence>
        {(isAnyDropdownOpen || isMobileOpen) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="fixed inset-0 z-20 bg-black/70 backdrop-blur-[1px]"
            onClick={() => setOpenMenu(null)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>
    </>
  );
}
